import { useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { MapPin, CalendarIcon, Trash2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import LocationPicker from './LocationPicker';

export default function EditClubRideModal({ ride, onClose, onUpdated, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(
    ride.date ? parseISO(ride.date) : null
  );
  const [selectedLocation, setSelectedLocation] = useState(
    ride.latitude && ride.longitude ? {
      lat: ride.latitude,
      lng: ride.longitude,
      address: ride.formatted_address || ''
    } : null
  );
  const [routeOptions, setRouteOptions] = useState(
    ride.route_options?.length > 0 
      ? ride.route_options 
      : [{ name: '', link: '' }]
  );
  const [formData, setFormData] = useState({
    time: ride.time || '',
    distance: ride.distance?.toString() || '',
    category: ride.category || '',
    meeting_point: ride.location || '',
    route_description: ride.route_description || '',
    max_riders: ride.max_riders?.toString() || ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const addRouteOption = () => {
    if (routeOptions.length < 3) {
      setRouteOptions([...routeOptions, { name: '', link: '' }]);
    }
  };

  const removeRouteOption = (index) => {
    if (routeOptions.length > 1) {
      setRouteOptions(routeOptions.filter((_, i) => i !== index));
    }
  };

  const updateRouteOption = (index, field, value) => {
    const updated = [...routeOptions];
    updated[index][field] = value;
    setRouteOptions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validRoutes = routeOptions.filter(r => r.name && r.link);

    const data = {
      date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ride.date,
      time: formData.time,
      distance: parseInt(formData.distance),
      category: formData.category,
      latitude: selectedLocation?.lat || ride.latitude,
      longitude: selectedLocation?.lng || ride.longitude,
      formatted_address: selectedLocation?.address || ride.formatted_address,
      location: formData.meeting_point,
      route_description: formData.route_description,
      max_riders: formData.max_riders ? parseInt(formData.max_riders) : null,
      route_options: validRoutes.length > 0 ? validRoutes : ride.route_options,
      route_link: validRoutes.length > 0 ? validRoutes[0].link : ride.route_link
    };

    try {
      await axios.put(`/rides/${ride.id}`, data);
      toast.success('Ride updated successfully!');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ride');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/rides/${ride.id}`);
      toast.success('Ride deleted');
      onDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ride');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Club Ride</DialogTitle>
            </DialogHeader>

            {showDeleteConfirm ? (
              <div className="py-6 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Delete this ride?</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  This action cannot be undone. All participants will be notified.
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1" 
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete Ride'}
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Date with Calendar */}
                <div>
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time */}
                <div>
                  <Label htmlFor="time">Start Time</Label>
                  <Input
                    id="time"
                    type="time"
                    required
                    value={formData.time}
                    onChange={(e) => updateFormField('time', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="distance">Distance (km)</Label>
                    <Input
                      id="distance"
                      type="number"
                      required
                      min="5"
                      max="500"
                      value={formData.distance}
                      onChange={(e) => updateFormField('distance', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_riders">Max Riders</Label>
                    <Input
                      id="max_riders"
                      type="number"
                      min="1"
                      max="100"
                      placeholder="No limit"
                      value={formData.max_riders}
                      onChange={(e) => updateFormField('max_riders', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="category">Ride Type</Label>
                  <select
                    id="category"
                    required
                    value={formData.category}
                    onChange={(e) => updateFormField('category', e.target.value)}
                    className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                  >
                    <option value="">Select ride type...</option>
                    <option value="road">Road</option>
                    <option value="mountain">Mountain</option>
                    <option value="gravel">Gravel</option>
                    <option value="commute">Commute</option>
                  </select>
                </div>

                <div>
                  <Label>Start Location</Label>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-1 justify-start text-left h-auto py-3"
                    onClick={() => setShowLocationPicker(true)}
                  >
                    <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                    {selectedLocation ? (
                      <span className="truncate text-foreground">{selectedLocation.address}</span>
                    ) : (
                      <span className="text-muted-foreground">Tap to change location</span>
                    )}
                  </Button>
                </div>

                <div>
                  <Label htmlFor="meeting_point">Meeting Point Details</Label>
                  <Input
                    id="meeting_point"
                    placeholder="e.g., Car park entrance"
                    value={formData.meeting_point}
                    onChange={(e) => updateFormField('meeting_point', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="route_description">Route Description</Label>
                  <Textarea
                    id="route_description"
                    required
                    rows={2}
                    value={formData.route_description}
                    onChange={(e) => updateFormField('route_description', e.target.value)}
                  />
                </div>

                {/* Route Options */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Route Options</Label>
                    {routeOptions.length < 3 && (
                      <Button type="button" variant="ghost" size="sm" onClick={addRouteOption}>
                        <Plus className="w-4 h-4 mr-1" /> Add Route
                      </Button>
                    )}
                  </div>
                  {routeOptions.map((route, index) => (
                    <div key={index} className="p-3 bg-gray-800/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Route {index + 1}</span>
                        {routeOptions.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeRouteOption(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        )}
                      </div>
                      <Input
                        placeholder="Route name"
                        value={route.name}
                        onChange={(e) => updateRouteOption(index, 'name', e.target.value)}
                      />
                      <Input
                        placeholder="Route link"
                        value={route.link}
                        onChange={(e) => updateRouteOption(index, 'link', e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 pt-2">
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Ride
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      )}

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
